// src/utils/errorHandler.tsx
import React, { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import { ApiError } from '../api/http';
import i18n from '../i18n';
import { useNotifications } from '../ui/notifications';

// --- 1. CLASE DEBOUNCER (UNIFICADA) ---
class Debouncer {
  private readonly timeout: number;
  private lastExecution: Map<string, number> = new Map();

  constructor(timeout: number) {
    this.timeout = timeout;
  }

  debounce(key: string, func: () => void) {
    const now = Date.now();
    const last = this.lastExecution.get(key) || 0;
    if (now - last > this.timeout) {
      this.lastExecution.set(key, now);
      func();
    }
  }
}

// --- 2. LÓGICA CENTRAL DE MANEJO DE ERRORES ---
const errorDebouncer = new Debouncer(1000);
export type ErrorContext = { [key: string]: any };

function notifyError(title: string, description: string, errorId: string) {
  const event = new CustomEvent('app-error', {
    detail: { title, description, errorId },
  });
  window.dispatchEvent(event);
}

export function logError(error: any, context?: ErrorContext, errorId?: string) {
  if (process.env.NODE_ENV === 'development') {
    console.error('Logged Error:', {
      id: errorId,
      timestamp: new Date().toISOString(),
      message: error.message || 'No message',
      stack: error.stack,
      context,
    });
  }
}

export function handleError(error: unknown, context: ErrorContext = { type: 'Generic' }) {
  const errorId = `err-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  let title = context.type || 'Error Inesperado';
  let description = i18n.t('manejadorErrores.unexpectedError');

  if (error instanceof ApiError) {
    const status = error.status;
    const translationKey = `manejadorErrores.apiErrors.${status}`;
    const genericApiErrorKey = 'manejadorErrores.apiErrors.generic';

    title = i18n.t('Error de API');
    // Usamos el `exists` de i18next para ver si tenemos una traducción específica
    if (i18n.exists(translationKey)) {
      description = i18n.t(translationKey);
    } else {
      // Si no hay una traducción específica, usamos el mensaje del error si existe, o uno genérico
      description = error.message || i18n.t(genericApiErrorKey);
    }
  } else if (error instanceof Error) {
    // Para errores de red genéricos que no son ApiError
    if (error.message.toLowerCase().includes('failed to fetch')) {
        title = i18n.t('Error de Red');
        description = i18n.t('manejadorErrores.apiErrors.network');
    } else {
        description = error.message;
    }
  } else if (typeof error === 'string') {
    description = error;
  }

  errorDebouncer.debounce(description, () => {
    notifyError(title, description, errorId);
  });

  logError(error, context, errorId);
}

// --- 3. WRAPPERS Y HELPERS (UNIFICADOS) ---
export function safeAsync<T extends (...args: any[]) => Promise<any>>(fn: T) {
  return async (...args: Parameters<T>): Promise<ReturnType<T> | void> => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, { type: 'Async Function', functionName: fn.name });
    }
  };
}

export function safeEvent<T extends (...args: any[]) => any>(fn: T) {
  return (...args: Parameters<T>): ReturnType<T> | void => {
    try {
      return fn(...args);
    } catch (error) {
      handleError(error, { type: 'Event Handler', functionName: fn.name });
    }
  };
}

export async function safeFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    const response = await window.fetch(input, init);
    if (!response.ok) {
      const errorBody = await response.text();
      throw new ApiError(response.status, errorBody || `HTTP error! Status: ${response.status}`);
    }
    return response;
  } catch (error) {
    handleError(error, { type: 'Network Request' });
    throw error;
  }
}

// --- 4. OYENTES GLOBALES (UNIFICADOS) ---
export function setupGlobalErrorHandlers() {
  window.onerror = (message, source, lineno, colno, error) => {
    handleError(error || new Error(message.toString()), { type: 'window.onerror', source, lineno, colno });
    return true;
  };
  window.addEventListener('error', (event) => {
    handleError(event.error, { type: 'window.error' });
    event.preventDefault();
  });
  window.addEventListener('unhandledrejection', (event) => {
    handleError(event.reason, { type: 'Unhandled Promise Rejection' });
    event.preventDefault();
  });
}

// --- 5. COMPONENTE ERROR BOUNDARY (UNIFICADO) ---
interface ErrorBoundaryProps {
  children: ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
}
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    handleError(error, { type: 'React Component', componentStack: errorInfo.componentStack });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, textAlign: 'center', background: '#fff1f2', color: '#d946ef' }}>
          <h1>Algo salió mal.</h1>
          <p>Hemos sido notificados y estamos investigando. Por favor, refresca la página.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// --- 6. COMPONENTE OYENTE DE NOTIFICACIONES (UNIFICADO) ---
export function ErrorNotificationListener() {
  const { showError } = useNotifications();

  useEffect(() => {
    const handleErrorEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { title, description } = customEvent.detail;
      showError(title, description);
    };
    window.addEventListener('app-error', handleErrorEvent);
    return () => {
      window.removeEventListener('app-error', handleErrorEvent);
    };
  }, [showError]);

  return null; // Este componente no renderiza nada.
}
